import { TwoFactorVerification } from '@/components/TwoFactorVerification';
import { verifyTwoFactorCode } from '@/lib/api';

interface BuyFlowStep3ContentProps {
  onStateChange: (data: { verified: boolean; escrowChecked: boolean }) => void;
}

/**
 * Buy Flow Step 3 Content: 2FA Verification
 * 
 * Content component for Step 3, designed to work within WizardLayout.
 * Handles 2FA verification and reports state changes to parent wizard.
 * WizardLayout provides the navigation buttons, so TwoFactorVerification's
 * Continue button is hidden.
 */
export const BuyFlowStep3Content = ({ onStateChange }: BuyFlowStep3ContentProps) => {
  const handleVerify = async (email: string, code: string): Promise<boolean> => {
    try {
      const result = await verifyTwoFactorCode(email, code);
      return result.verified;
    } catch (error) {
      // Error handling is done in TwoFactorVerification component
      // Log error for debugging if needed (only in development)
      if (process.env.NODE_ENV === 'development' && error instanceof Error) {
        console.error('2FA verification error:', {
          message: error.message,
          email: email,
          codeLength: code?.length || 0,
        });
      }
      return false;
    }
  };

  const handleComplete = () => {
    // This is called by TwoFactorVerification when verification completes
    // Navigation is handled by WizardLayout, so this is a no-op
    // State changes are reported via onStateChange callback
  };

  return (
    <div>
      {/* Title */}
      <div className="text-center mb-2">
        <h1 className="text-h1 font-bold text-text-primary">Verification</h1>
      </div>

      {/* Instruction */}
      <div className="text-center mb-8">
        <p className="text-base text-text-secondary">
          Enter your email to receive a verification code
        </p>
      </div>

      {/* TwoFactorVerification Component - Hide Continue button, report state changes */}
      <TwoFactorVerification
        onVerify={handleVerify}
        showEscrowCheckbox={true}
        onComplete={handleComplete}
        hideContinueButton={true}
        onStateChange={onStateChange}
      />
    </div>
  );
};

